/**
 * Une ligne de la liste /cee/inbox (PJ non resolue).
 *
 * Affiche : date recue, expediteur, sujet mail, nom fichier, type detecte
 * (avec confidence si < 0.85), client(s) suggere(s), apercu PDF inline (toggle),
 * actions Affecter / Reclasser / Ignorer.
 *
 * Le composant est presentational ; les callbacks d action sont fournis par
 * le parent (CeeInboxPage).
 */

import { useState } from 'react';
import { timeAgo } from '../lib/formatBytes.js';
import { DOC_TYPE_META } from '../lib/docTypes.js';
import ClientAutocomplete from './ClientAutocomplete.jsx';

// Codex P2 review : la route GET /api/cee/inbox/:id/preview n est pas
// implementee (signed URL ou cookie session requis pour iframe sans header
// Authorization). Bouton Apercu desactive jusqu a livraison de cette route
// (P2 backlog). Eviter route-miss/404 en cliquant sur un PDF inbox.
const PREVIEW_ENABLED = false;

/**
 * @param {{
 *   item: object,
 *   busy?: boolean,
 *   onAffect: (eventId: string, dossierId: string, docType: string) => void,
 *   onReclassify: (eventId: string, docType: string) => void,
 *   onIgnore: (eventId: string) => void,
 * }} props
 */
export default function InboxRow({ item, busy = false, onAffect, onReclassify, onIgnore }) {
  const [previewing, setPreviewing] = useState(false);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [docType, setDocType] = useState(item.detectedDocType || 'autre');

  const confidence = typeof item.ocrConfidence === 'number' ? item.ocrConfidence : null;
  const lowConfidence = confidence !== null && confidence < 0.85;
  const isPdf = PREVIEW_ENABLED && (
    (item.attachmentMime === 'application/pdf') || (item.attachmentFilename || '').toLowerCase().endsWith('.pdf')
  );

  const handleAffect = () => {
    if (!selectedDossier) return;
    onAffect(item.id, selectedDossier.dossierId, docType);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 mb-3 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-[#94a3b8]">{timeAgo(item.createdAt)} · {item.senderEmail || 'expediteur inconnu'}</div>
          <div className="text-sm font-medium text-[#0f172a] truncate" title={item.subject}>{item.subject || '(sans sujet)'}</div>
          <div className="text-xs text-[#475569] truncate" title={item.attachmentFilename}>
            {item.attachmentFilename || '(fichier sans nom)'}
            {confidence !== null && (
              <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                lowConfidence ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'
              }`}>
                {item.detectedDocType || 'autre'} · {(confidence * 100).toFixed(0)}%
              </span>
            )}
          </div>
        </div>
        {isPdf && (
          <button
            type="button"
            onClick={() => setPreviewing((v) => !v)}
            className="text-xs px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-[#0f172a] rounded-md font-medium"
          >
            {previewing ? 'Masquer' : 'Apercu'}
          </button>
        )}
      </div>

      {/* Preview iframe desactivee (Codex P2 review) — voir constante PREVIEW_ENABLED */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
        <ClientAutocomplete onSelect={setSelectedDossier} placeholder="Client suggere ou recherche..." />
        <select
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          {DOC_TYPE_META.map((d) => (
            <option key={d.key} value={d.key}>{d.label}</option>
          ))}
        </select>
        <div className="flex gap-2">
          <button type="button" onClick={handleAffect} disabled={busy || !selectedDossier}
            className="flex-1 px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed">
            Affecter
          </button>
          <button type="button" onClick={() => onReclassify(item.id, docType)} disabled={busy}
            className="px-3 py-2 text-sm bg-white border border-gray-200 text-[#475569] rounded-lg hover:bg-gray-50 disabled:opacity-50">
            Reclasser
          </button>
          <button type="button" onClick={() => onIgnore(item.id)} disabled={busy}
            className="px-3 py-2 text-sm bg-white border border-red-200 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50">
            Ignorer
          </button>
        </div>
      </div>
    </div>
  );
}
